import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Typography,
  Button,
  Row,
  Col,
  Tag,
  Input,
  Select,
  Modal,
  Form,
  Statistic,
  List,
  Rate,
  Alert,
  Spin,
  Space,
  Badge,
} from 'antd';
import {
  DollarOutlined,
  SearchOutlined,
  PlusOutlined,
  StarOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { config } from '../config/env';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

interface DataListing {
  id: string;
  title: string;
  description: string;
  dataType: string;
  location: any;
  dataPoints: number;
  qualityScore: number;
  verified: boolean;
  pricingModel: string;
  price?: number;
  creditsPrice?: number;
  privacyLevel: string;
  accessCount: number;
  host: {
    id: string;
    name: string;
  };
  reviews: Array<{
    rating: number;
    comment?: string;
  }>;
  _count: {
    purchases: number;
  };
}

export const DataMarketplace: React.FC = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState<DataListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    dataType: undefined as string | undefined,
    pricingModel: undefined as string | undefined,
    search: '',
  });

  const fetchListings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params: any = {};
      if (filters.dataType) params.dataType = filters.dataType;
      if (filters.pricingModel) params.pricingModel = filters.pricingModel;
      if (filters.search) params.search = filters.search;

      const response = await axios.get(`${config.apiUrl}/api/marketplace/listings`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setListings(response.data);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [filters]);

  const handlePurchase = async (listingId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${config.apiUrl}/api/marketplace/listings/${listingId}/purchase`,
        { paymentMethod: 'CREDITS' },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      alert('Purchase successful! Access your data in "My Purchases"');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Purchase failed');
    }
  };

  const handleCreateListing = async (values: any) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${config.apiUrl}/api/marketplace/listings`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCreateModalVisible(false);
      form.resetFields();
      await fetchListings();
    } catch (error) {
      console.error('Failed to create listing:', error);
    }
  };

  const getAverageRating = (reviews: Array<{ rating: number }>) => {
    if (!reviews || reviews.length === 0) return 0;
    return reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#ffffff' }}>
      <Content style={{ padding: '24px', background: '#ffffff' }}>
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>Data Marketplace</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            List Your Data
          </Button>
        </div>

        <Card style={{ marginBottom: '24px', background: '#ffffff', border: '1px solid #e8e8e8' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Search
              placeholder="Search datasets..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={(value) => setFilters({ ...filters, search: value })}
            />
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Data Type"
                  style={{ width: '100%' }}
                  allowClear
                  onChange={(value) => setFilters({ ...filters, dataType: value })}
                >
                  <Option value="ENERGY">Energy</Option>
                  <Option value="CROP">Crop</Option>
                  <Option value="WEATHER">Weather</Option>
                  <Option value="SOIL">Soil</Option>
                  <Option value="COMBINED">Combined</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Pricing Model"
                  style={{ width: '100%' }}
                  allowClear
                  onChange={(value) => setFilters({ ...filters, pricingModel: value })}
                >
                  <Option value="FREE">Free</Option>
                  <Option value="ONE_TIME">One-Time</Option>
                  <Option value="SUBSCRIPTION">Subscription</Option>
                  <Option value="PAY_PER_ACCESS">Pay Per Access</Option>
                </Select>
              </Col>
            </Row>
          </Space>
        </Card>

        {loading ? (
          <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
        ) : (
          <Row gutter={[24, 24]}>
            {listings.map((listing) => (
              <Col xs={24} sm={12} lg={8} key={listing.id}>
                <Card
                  hoverable
                  style={{ background: '#ffffff', border: '1px solid #e8e8e8' }}
                  actions={[
                    <Button
                      type="primary"
                      icon={<ShoppingCartOutlined />}
                      onClick={() => handlePurchase(listing.id)}
                    >
                      {listing.pricingModel === 'FREE' ? 'Get Free' : 'Purchase'}
                    </Button>,
                  ]}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                      {listing.title}
                    </Title>
                    <Space style={{ marginTop: '8px' }}>
                      <Tag>{listing.dataType}</Tag>
                      {listing.verified && (
                        <Tag color="green" icon={<StarOutlined />}>
                          Verified
                        </Tag>
                      )}
                    </Space>
                  </div>

                  <Text style={{ display: 'block', marginBottom: '12px', color: '#595959' }}>
                    {listing.description}
                  </Text>

                  <Row gutter={[16, 16]} style={{ marginBottom: '12px' }}>
                    <Col span={12}>
                      <Statistic
                        title="Data Points"
                        value={listing.dataPoints}
                        valueStyle={{ fontSize: '14px' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Quality"
                        value={listing.qualityScore}
                        precision={1}
                        suffix="/ 10"
                        valueStyle={{ fontSize: '14px', color: listing.qualityScore >= 7 ? '#3f8600' : '#1890ff' }}
                      />
                    </Col>
                  </Row>

                  <div style={{ marginBottom: '12px' }}>
                    {listing.pricingModel === 'FREE' ? (
                      <Text strong style={{ fontSize: '16px', color: '#3f8600' }}>
                        Free
                      </Text>
                    ) : (
                      <div>
                        {listing.price && (
                          <Text strong style={{ fontSize: '16px' }}>
                            ${listing.price}
                          </Text>
                        )}
                        {listing.creditsPrice && (
                          <Text type="secondary" style={{ marginLeft: '8px' }}>
                            or {listing.creditsPrice} credits
                          </Text>
                        )}
                      </div>
                    )}
                  </div>

                  {listing.reviews && listing.reviews.length > 0 && (
                    <div>
                      <Rate
                        disabled
                        value={getAverageRating(listing.reviews)}
                        style={{ fontSize: '12px' }}
                      />
                      <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                        ({listing.reviews.length} reviews)
                      </Text>
                    </div>
                  )}

                  <div style={{ marginTop: '12px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      <EyeOutlined /> {listing.accessCount} accesses
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Modal
          title="Create Data Listing"
          open={createModalVisible}
          onOk={() => form.submit()}
          onCancel={() => {
            setCreateModalVisible(false);
            form.resetFields();
          }}
          width={600}
        >
          <Form form={form} onFinish={handleCreateListing} layout="vertical">
            <Form.Item name="title" label="Title" rules={[{ required: true }]}>
              <Input placeholder="e.g., California Farm Energy Data 2024" />
            </Form.Item>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}>
              <Input.TextArea rows={4} placeholder="Describe your dataset..." />
            </Form.Item>
            <Form.Item name="dataType" label="Data Type" rules={[{ required: true }]}>
              <Select>
                <Option value="ENERGY">Energy</Option>
                <Option value="CROP">Crop</Option>
                <Option value="WEATHER">Weather</Option>
                <Option value="SOIL">Soil</Option>
                <Option value="COMBINED">Combined</Option>
              </Select>
            </Form.Item>
            <Form.Item name="pricingModel" label="Pricing Model" rules={[{ required: true }]}>
              <Select>
                <Option value="FREE">Free</Option>
                <Option value="ONE_TIME">One-Time Payment</Option>
                <Option value="SUBSCRIPTION">Subscription</Option>
                <Option value="PAY_PER_ACCESS">Pay Per Access</Option>
              </Select>
            </Form.Item>
            <Form.Item name="price" label="Price ($)" dependencies={['pricingModel']}>
              {Form.useWatch('pricingModel') !== 'FREE' && (
                <Input type="number" placeholder="0.00" />
              )}
            </Form.Item>
            <Form.Item name="privacyLevel" label="Privacy Level" rules={[{ required: true }]}>
              <Select>
                <Option value="PUBLIC">Public</Option>
                <Option value="ANONYMIZED">Anonymized</Option>
                <Option value="PRIVATE">Private</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default DataMarketplace;


